import Api from '@/data/Api.ts';
import type { IssueCountsPerDateJson } from '@/data/companies/IssueCountsPerDateJson.ts';
import StringUtils from '@/infrastructure/support/utils/StringUtils.ts';
import { endOfDay, formatISO, startOfDay } from 'date-fns';
import type { DateRange } from '@/domain/DateRange.ts';

const API_URL_GET_COMPANY_BY_ID = '/companies/{0}';

const API_URL_GET_ALL_COMPANIES = '/companies';
const API_URL_GET_ALL_COMPANIES_CHILDREN = '/companies/{0}/children';
const API_URL_COMPANY_DRIVER_ACCOUNTS = '/companies/{0}/driver-accounts';
const API_URL_COMPANY_DRIVER_ACCOUNTS_VISIBLE =
  '/companies/{0}/visible-driver-accounts';
const API_URL_DRIVER_STATUS = '/companies/{0}/driver-status';
const API_URL_GET_ALL_COMPANIES_OF_ME = '/users/me/companies';

const API_URL_UPDATE_COMPANY = 'companies/{0}';
const API_URL_CREATE_COMPANY = 'companies';

const API_URL_COMPANY_ISSUES_FOUND_PER_DATE = '/companies/{0}/issues-per-date';
const API_URL_COMPANY_ISSUES_RESOLVED_PER_DATE =
  '/companies/{0}/issues-resolved-per-date';
const API_URL_COMPANY_PROMISE_STATUS_CHANGES_PER_DATE =
  '/companies/{0}/promise-status-changes-per-date';

type IssueType = 'found_on' | 'resolved_on';

export default class CompanyApi extends Api {
  /**
   * Find a company by id.
   *
   * @param companyId The associated company id.
   * @param params
   * @param includes An array of relations to include with the entity e.g. ['children', 'parents'].
   * @returns {Promise} A promise containing the response, or a thrown error.
   */
  findById(companyId: number, params = {}, includes = []): Promise<any> {
    let url = StringUtils.stringFormat(API_URL_GET_COMPANY_BY_ID, [companyId]);
    return this.standardGet(url, params, includes);
  }

  /**
   * Get all children from a company.
   *
   * @param params The query parameters.
   * @param includes An array of relations to include with the entity e.g. ['children', 'parents'].
   * @returns {Promise} A promise containing the response, or a thrown error.
   */
  allOfMe(params = {}, includes = []): Promise<any> {
    return this.standardGet(API_URL_GET_ALL_COMPANIES_OF_ME, params, includes);
  }

  /**
   * Get all companies. Only admin auth.
   * @returns {Promise} A promise containing the response, or a thrown error.
   */
  all(): Promise<any> {
    return this.standardGet(API_URL_GET_ALL_COMPANIES, {}, ['parents']);
  }

  /**
   * Get all children from a company.
   *
   * @param companyId The associated company id.
   * @param params
   * @param includes An array of relations to include with the entity e.g. ['children', 'parents'].
   * @returns {Promise} A promise containing the response, or a thrown error.
   */
  allChildrenBy(companyId: number, params = {}, includes = []): Promise<any> {
    let url = StringUtils.stringFormat(API_URL_GET_ALL_COMPANIES_CHILDREN, [
      companyId,
    ]);
    return this.standardGet(url, params, includes);
  }

  driverStatus(companyId: number): Promise<any> {
    let url = StringUtils.stringFormat(API_URL_DRIVER_STATUS, [companyId]);
    return this.standardGet(url, {}, []);
  }

  driverAccounts(companyId: number): Promise<any> {
    return this.standardGet(
      StringUtils.stringFormat(API_URL_COMPANY_DRIVER_ACCOUNTS, [companyId]),
      {},
      [],
    );
  }

  visibleDriverAccounts(companyId: number): Promise<any> {
    return this.standardGet(
      StringUtils.stringFormat(API_URL_COMPANY_DRIVER_ACCOUNTS_VISIBLE, [
        companyId,
      ]),
      {},
      [],
    );
  }

  /**
   * @returns {Promise}
   * @param companyData
   */
  create(companyData = {}): Promise<any> {
    return this.standardPost(API_URL_CREATE_COMPANY, companyData);
  }

  /**
   * Updates a company,  don't forget the payload ;)
   *
   * @param companyId
   * @param payload
   * @returns {Promise}
   */
  update(companyId: number, payload = {}): Promise<any> {
    let url = StringUtils.stringFormat(API_URL_UPDATE_COMPANY, [companyId]);
    return this.standardPut(url, payload);
  }

  issueCountsPerDate(
    companyId: number,
    dateRange: DateRange,
    type: IssueType,
    includeResolved: boolean,
  ) {
    const url =
      type === 'found_on'
        ? StringUtils.stringFormat(API_URL_COMPANY_ISSUES_FOUND_PER_DATE, [
            companyId,
          ])
        : StringUtils.stringFormat(API_URL_COMPANY_ISSUES_RESOLVED_PER_DATE, [
            companyId,
          ]);

    return this.standardGet<Record<string, IssueCountsPerDateJson>>(
      url,
      {
        start_date: formatISO(startOfDay(dateRange.startDate)),
        end_date: formatISO(endOfDay(dateRange.endDate)),
        include_resolved: includeResolved,
      },
      [],
    );
  }

  promiseStatusChangesPerDate(
    companyId: number,
    dateRange: DateRange,
  ): Promise<any> {
    return this.standardGet(
      StringUtils.stringFormat(
        API_URL_COMPANY_PROMISE_STATUS_CHANGES_PER_DATE,
        [companyId],
      ),
      {
        start_date: formatISO(startOfDay(dateRange.startDate)),
        end_date: formatISO(endOfDay(dateRange.endDate)),
      },
      [],
    );
  }
}
